<script setup lang="ts">
    import { useEventListener } from '@vueuse/core';
    import { Modal, ModalContent } from '@/shared/ui/organisms/Modal';
    import Command from './Command.vue';
    import { commandSelectKey } from './context';
    import type { CommandFilter } from './filter';
    import { matchesShortcut } from './shortcut';

    const props = withDefaults(
        defineProps<{
            /** Opens it on mount: a dialog opened from code, as the dialogs store does */
            defaultOpen?: boolean;
            /** The key press that opens and closes it from anywhere on the page, or several. `null` turns it off */
            shortcut?: string | readonly string[] | null;
            /** Selecting an item closes it */
            closeOnSelect?: boolean;
            /** The panel's maximum width, as on ModalContent */
            size?: 'sm' | 'md' | 'lg' | 'xl' | 'full';
            /** The search text. Leave unset to start each opening with an empty search */
            search?: string;
            /** The active item's value */
            value?: string;
            filter?: CommandFilter;
            shouldFilter?: boolean;
            loop?: boolean;
        }>(),
        {
            defaultOpen: false,
            shortcut: 'mod+K',
            closeOnSelect: true,
            size: 'lg',
            search: undefined,
            value: undefined,
            filter: undefined,
            shouldFilter: true,
            loop: true,
        },
    );

    const emit = defineEmits<{
        /** It opened or closed. There is no `open` prop: the dialog keeps its own state */
        'update:open': [open: boolean];
        'update:search': [search: string];
        'update:value': [value: string | undefined];
    }>();

    defineSlots<{
        /** The palette: the Command parts. `close` closes it */
        default?(props: { close: () => void }): unknown;
        /** What opens it: any component that calls `open`. Bind `triggerAttrs` on it for `aria-expanded`, `aria-controls` and the id focus returns to */
        trigger?(props: { open: () => void; triggerAttrs: Record<string, unknown> }): unknown;
    }>();

    const isOpen = ref(props.defaultOpen);

    function setOpen(next: boolean) {
        if (next === isOpen.value) return;
        isOpen.value = next;
        emit('update:open', next);
    }

    const open = () => setOpen(true);
    const close = () => setOpen(false);

    /* The Modal's trigger attributes without its click handler: the trigger calls `open` itself. */
    const withoutClick = ({ onClick: _, ...attrs }: Record<string, unknown>) => attrs;

    defineOptions({ inheritAttrs: false });

    const { t } = useI18n();
    const attrs = useAttrs();

    /* The shortcut works anywhere on the page, also in a field. */
    useEventListener(typeof document === 'undefined' ? null : document, 'keydown', (event: KeyboardEvent) => {
        if (event.defaultPrevented || event.repeat || props.shortcut === null) return;
        const shortcuts = typeof props.shortcut === 'string' ? [props.shortcut] : props.shortcut;
        if (!shortcuts.some((shortcut) => matchesShortcut(event, shortcut))) return;
        event.preventDefault();
        setOpen(!isOpen.value);
    });

    provide(commandSelectKey, () => {
        if (props.closeOnSelect) close();
    });

    /* `search` and `value` reach the Command only when set, so an unset one stays the Command's own and starts fresh on each opening. */
    const commandModels = computed(() => ({
        ...(props.search === undefined ? {} : { search: props.search }),
        ...(props.value === undefined ? {} : { value: props.value }),
        'onUpdate:search': (search: string) => emit('update:search', search),
        'onUpdate:value': (value: string | undefined) => emit('update:value', value),
    }));

    const ariaLabel = computed(() => (attrs['aria-label'] || attrs['aria-labelledby'] ? attrs['aria-label'] : t('COMMAND_MENU')));
</script>

<template>
    <Modal
        v-slot="{ triggerAttrs }"
        :open="isOpen"
        @update:open="setOpen"
    >
        <slot
            name="trigger"
            :open="open"
            :trigger-attrs="withoutClick(triggerAttrs)"
        />
        <!-- The panel is removed while closed, so the Command mounts again on each opening -->
        <ModalContent
            v-bind="$attrs"
            :size="size"
            :aria-label="ariaLabel"
            class="ui-command-dialog"
        >
            <Command
                v-bind="commandModels"
                :filter="filter"
                :should-filter="shouldFilter"
                :loop="loop"
            >
                <slot :close="close" />
            </Command>
        </ModalContent>
    </Modal>
</template>

<style scoped>
    @layer components {
        /* The modal is the surface */
        .ui-command-dialog > .ui-command {
            flex: 1 1 auto;
            min-height: 0;
            border: 0;
            border-radius: 0;
            background-color: transparent;
        }
    }
</style>

<i18n lang="yaml">
en:
    COMMAND_MENU: Command menu
nl:
    COMMAND_MENU: Opdrachtmenu
</i18n>
